/**
 * Hooks local-first de la COMUNIDAD (F3-C): foros, anuncios, notificaciones y
 * badges. Los datos llegan por los streams de curso/cohorte (forum/thread/post/
 * announcement) y privados (my_notifications/my_badges). La escritura pasa por
 * el write-path (moderación / read_at-only / server-only para badges).
 */
import type { Accessor } from "solid-js";
import {
  communityQueryKeys,
  type ForumCohortContext,
  getAmICourseAuthorQuery,
  getAnnouncementsByCohortQuery,
  getAnnouncementsByOrgQuery,
  getForumByModuleQuery,
  getForumThreadsQuery,
  getMyBadgesQuery,
  getMyNotificationsQuery,
  getMyStudentCohortForCourseQuery,
  getMyTeachingCohortForCourseQuery,
  getThreadPostsQuery,
  getUnreadNotificationsQuery,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type {
  Announcement,
  BadgeAward,
  Forum,
  ForumPost,
  ForumThread,
  Notification,
} from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

// ─── Foro ─────────────────────────────────────────────────────────────────────

export function useForumByModule(moduleId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Forum>({
    queryKey: () => communityQueryKeys.forumByModule(moduleId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = moduleId();
      return db && id ? getForumByModuleQuery(db, id) : null;
    },
    enabled: () => Boolean(moduleId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useForumThreads(
  forumId: Accessor<string | undefined>,
  cohortId: Accessor<string | null>,
) {
  const base = useBase();
  return createPowerSyncQuery<ForumThread>({
    queryKey: () => communityQueryKeys.threads(forumId(), cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = forumId();
      return db && id ? getForumThreadsQuery(db, id, cohortId()) : null;
    },
    enabled: () => Boolean(forumId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useThreadPosts(threadId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ForumPost>({
    queryKey: () => communityQueryKeys.posts(threadId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = threadId();
      return db && id ? getThreadPostsQuery(db, id) : null;
    },
    enabled: () => Boolean(threadId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// ─── Contexto de cohorte del visor ────────────────────────────────────────────

export function useMyStudentCohortForCourse(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<ForumCohortContext>({
    queryKey: () => communityQueryKeys.studentCohort(courseId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const cid = courseId();
      const uid = base.userId();
      return db && cid && uid ? getMyStudentCohortForCourseQuery(db, uid, cid) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useMyTeachingCohortForCourse(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<ForumCohortContext>({
    queryKey: () => communityQueryKeys.teachingCohort(courseId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const cid = courseId();
      const uid = base.userId();
      return db && cid && uid ? getMyTeachingCohortForCourseQuery(db, uid, cid) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useAmICourseAuthor(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<{ id: string }>({
    queryKey: () => communityQueryKeys.courseAuthor(courseId(), base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const cid = courseId();
      const uid = base.userId();
      return db && cid && uid ? getAmICourseAuthorQuery(db, uid, cid) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// ─── Notificaciones + badges ──────────────────────────────────────────────────

export function useMyNotifications() {
  const base = useBase();
  return createPowerSyncQuery<Notification>({
    queryKey: () => communityQueryKeys.notifications(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyNotificationsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useUnreadNotifications() {
  const base = useBase();
  return createPowerSyncQuery<Notification>({
    queryKey: () => communityQueryKeys.unread(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getUnreadNotificationsQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useMyBadges() {
  const base = useBase();
  return createPowerSyncQuery<BadgeAward>({
    queryKey: () => communityQueryKeys.badges(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyBadgesQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

// ─── Anuncios ─────────────────────────────────────────────────────────────────

export function useAnnouncementsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Announcement>({
    queryKey: () => communityQueryKeys.announcementsByOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getAnnouncementsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useAnnouncementsByCohort(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Announcement>({
    queryKey: () => communityQueryKeys.announcementsByCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getAnnouncementsByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
